import React, { useEffect, useState, useCallback } from 'react';
import { 
  FileText, Search, Loader, AlertCircle, CheckCircle, 
  Eye, ChevronLeft, ChevronRight, 
  Activity, Shield, CreditCard,
  Plus, Check, Banknote, Receipt, Clock, Ban,
  RefreshCw
} from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';

// Cross-tenant aggregate view — no single "company currency" applies here;
// platform billing is always reported in the platform's own currency.
const PLATFORM_CURRENCY = 'INR';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'issued', label: 'Pending' },
  { value: 'paid', label: 'Settled' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Void' }
];

function InvBadge({ status }) {
  const variants = {
    paid: 'emerald',
    issued: 'amber',
    overdue: 'rose',
    cancelled: 'slate',
    default: 'slate'
  };
  const labels = {
    paid: 'Settled',
    issued: 'Pending',
    overdue: 'Overdue',
    cancelled: 'Void',
  };
  return <Badge variant={variants[status] || variants.default} className="text-[11px] font-semibold">{labels[status] || 'Draft'}</Badge>;
}

function CreateInvoiceModal({ isOpen, onClose, onCreated }) {
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ companyId: '', planId: '', durationDays: '30', notes: '', discountType: 'flat', discountValue: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      Promise.all([
        api.get('/admin/companies', { params: { limit: 100 } }),
        api.get('/admin/plans'),
      ]).then(([cRes, pRes]) => {
        setCompanies(cRes.data.companies || []);
        setPlans(pRes.data.plans || []);
      }).catch(() => { });
    }
  }, [isOpen]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedPlan = plans.find(p => p._id === form.planId);

  const handleCreate = async () => {
    if (!form.companyId || !form.planId) {
      setError('Client identity and subscription plan required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await api.post('/admin/invoices', form);
      alertService.toast('success', 'Fiscal record generated successfully');
      onCreated();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to initialize settlement record.');
    } finally {
      setSaving(false);
    }
  };

  const rawDiscount = selectedPlan
    ? (form.discountType === 'percentage'
      ? selectedPlan.price * (parseFloat(form.discountValue) || 0) / 100
      : (parseFloat(form.discountValue) || 0))
    : 0;
  const discount = selectedPlan ? Math.min(Math.max(rawDiscount, 0), selectedPlan.price) : 0;
  const discountedBase = selectedPlan ? selectedPlan.price - discount : 0;
  const tax = selectedPlan ? discountedBase * 0.18 : 0;
  const total = selectedPlan ? discountedBase + tax : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Fiscal Record"
      subtitle="Create an invoice for a customer"
      icon={Receipt}
      footer={
        <div className="flex justify-end gap-2">
          <button className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2] transition-colors" onClick={onClose}>Cancel</button>
          <button className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded flex items-center justify-center gap-2 transition-colors" onClick={handleCreate} disabled={saving}>
            {saving ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
            Generate Invoice
          </button>
        </div>
      }
    >
      <div className="space-y-6 p-2">
        {error && (
          <div className="p-3 rounded-md bg-[#FEF1EE] border border-[#FDDDE3] flex items-center gap-3 text-[#BA0517] text-[13px] font-semibold">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <div className="space-y-2">
          <label className="text-[12px] font-semibold text-[#444444]">Client Identity</label>
          <Select
            value={form.companyId}
            onChange={e => set('companyId', e.target.value)}
          >
            <option value="">Select Entity Identification…</option>
            {companies.map(c => (
               <option key={c._id} value={c._id}>{String(c.companyName || c.name)} ({c.email})</option>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
           <label className="text-[12px] font-semibold text-[#444444]">Subscription Plan</label>
           <Select
             value={form.planId}
             onChange={e => set('planId', e.target.value)}
           >
             <option value="">Select a plan…</option>
             {plans.map(p => (
                <option key={p._id} value={p._id}>{p.name} — {formatCurrency(p.price, PLATFORM_CURRENCY)}</option>
             ))}
           </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
           <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Settlement Duration (Days)</label>
              <Input
                type="number"
                min="1"
                icon={Clock}
                value={form.durationDays}
                onChange={e => set('durationDays', e.target.value)}
              />
           </div>
           <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Internal Reference</label>
              <Input
                placeholder="Notes / Ref"
                icon={FileText}
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
              />
           </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
           <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Discount Type</label>
              <Select
                value={form.discountType}
                onChange={e => set('discountType', e.target.value)}
              >
                <option value="flat">Flat Amount</option>
                <option value="percentage">Percentage</option>
              </Select>
           </div>
           <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#444444]">Discount Value</label>
              <Input
                type="number"
                min="0"
                placeholder={form.discountType === 'percentage' ? '%' : formatCurrency(0, PLATFORM_CURRENCY)}
                value={form.discountValue}
                onChange={e => set('discountValue', e.target.value)}
              />
           </div>
        </div>

        {selectedPlan && (
          <div className="p-6 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] space-y-4 shadow-sm">
            <div className="flex justify-between items-center text-[13px] font-semibold text-[#080707] leading-none">
              <span>Base Price</span>
              <span className="tabular-nums text-[#080707]">{formatCurrency(selectedPlan.price, PLATFORM_CURRENCY)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between items-center text-[13px] font-semibold text-[#2E844A] leading-none">
                <span>Discount {form.discountType === 'percentage' ? `(${form.discountValue}%)` : ''}</span>
                <span className="tabular-nums">-{formatCurrency(discount, PLATFORM_CURRENCY)}</span>
              </div>
            )}
            <div className="flex justify-between items-center text-[13px] font-semibold text-[#080707] leading-none">
              <span>Applied Taxation (18%)</span>
              <span className="tabular-nums text-[#080707]">{formatCurrency(tax, PLATFORM_CURRENCY)}</span>
            </div>
            <div className="pt-4 border-t border-[#D8DDE6] flex justify-between items-center">
              <span className="text-[14px] font-bold text-[#080707]">Total Settlement Value</span>
              <span className="text-[18px] font-bold text-[#080707] tabular-nums">{formatCurrency(total, PLATFORM_CURRENCY)}</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function SuperAdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [creating, setCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const LIMIT = 20;

  const load = useCallback(async (p = 1, s = statusFilter) => {
    try {
      setLoading(true);
      setError('');
      const params = { page: p, limit: LIMIT };
      if (s) params.status = s;
      const res = await api.get('/admin/invoices', { params });
      setInvoices(res.data.invoices || []);
      setTotal(res.data.pagination?.total || 0);
      setPage(p);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to sync financial registry.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(1, statusFilter); }, [load, statusFilter]);

  const handleMarkPaid = async (invoiceId) => {
    const confirmed = await alertService.confirm(
      'Settle Fiscal Record?',
      'Authorize this transaction as confirmed. This will update the network ledger and activate client protocols.',
      'success',
      'Mark as Settled'
    );
    if (!confirmed) return;

    try {
      await api.put(`/admin/invoices/${invoiceId}/status`, { status: 'paid' });
      alertService.toast('success', 'Fiscal record marked as settled');
      load(page, statusFilter);
    } catch (e) {
      alertService.error('Operation Failed', e.response?.data?.error || 'Failed to update record status.');
    }
  };

  const handleCancel = async (invoiceId) => {
    const confirmed = await alertService.confirm(
      'Void Fiscal Record?',
      'Warning: This will permanently deactivate this settlement record and purge all associated fiscal projections.',
      'danger',
      'Delete'
    );
    if (!confirmed) return;

    try {
      await api.put(`/admin/invoices/${invoiceId}/status`, { status: 'cancelled' });
      alertService.toast('success', 'Fiscal record purged successfully');
      load(page, statusFilter);
    } catch (e) {
      alertService.error('Operation Failed', e.response?.data?.error || 'Failed to cancel record.');
    }
  };

  const handleViewPdf = async (invoice) => {
    setDownloadingId(invoice._id);
    try {
      const res = await api.get(`/admin/invoices/${invoice._id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alertService.error('Fiscal Record Unavailable', e.response?.data?.error || 'Failed to generate invoice PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const getAmt = (i) => i.amount?.totalAmount ?? i.amount ?? 0;
  const pagePaidRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + getAmt(i), 0);
  const pageOutstanding = invoices.filter(i => i.status === 'issued').reduce((s, i) => s + getAmt(i), 0);

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader title="Billing Ledger" icon={Receipt} subtitle={`Platform invoices and settlements. Active records: ${total}`} />

       {/* Financial Metrics Layer */}
       <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {[
          { label: 'Total Collected', val: pagePaidRevenue, prefix: getCurrencySymbol(PLATFORM_CURRENCY), sub: `Sync: ${invoices.filter(i => i.status === 'paid').length} Settlements`, icon: CheckCircle, variant: 'success' },
          { label: 'Outstanding Credits', val: pageOutstanding, prefix: getCurrencySymbol(PLATFORM_CURRENCY), sub: `Pending: ${invoices.filter(i => i.status === 'issued').length} Settlements`, icon: Clock, variant: 'warning' },
          { label: 'Critical Overdue', val: invoices.filter(i => i.status === 'overdue').length, sub: 'Immediate manual intervention required', icon: AlertCircle, variant: 'danger' }
        ].map((kpi, i) => (
          <Card key={i} className="p-6 bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
             <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                   <div className={cn(
 "w-10 h-10 rounded-md flex items-center justify-center border",
 kpi.variant === 'success' ? 'bg-[#E0F2FE] text-[#0176D3] border-[#BAE6FD]' :
 kpi.variant === 'warning' ? 'bg-[#FEF3C7] text-[#D97706] border-[#FDE68A]' :
 'bg-[#FEE2E2] text-[#DC2626] border-[#FECACA]'
 )}>
                      <kpi.icon size={20} />
                   </div>
                   <p className="text-[12px] font-semibold text-[#706E6B]">{kpi.label}</p>
                </div>
                <div className="space-y-1">
                   <div className="text-2xl font-bold text-[#080707] tabular-nums">
                      {kpi.prefix}{kpi.val.toLocaleString('en-IN')}
                   </div>
                   <p className="text-[12px] text-[#706E6B]">{kpi.sub}</p>
                </div>
             </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div className="flex flex-wrap gap-2 p-1 bg-[#F4F6F9] border border-[#D8DDE6] rounded-md shadow-sm">
          {STATUS_OPTIONS.map(s => (
            <button
              key={s.value}
              className={cn(
 "px-4 py-2 rounded whitespace-nowrap text-[13px] font-semibold transition-colors",
 statusFilter === s.value ? 'bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]' : 'text-[#706E6B] hover:bg-white hover:text-[#080707]'
 )}
              onClick={() => setStatusFilter(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button 
           className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold rounded whitespace-nowrap flex items-center gap-2 transition-colors"
           onClick={() => setCreating(true)}
        >
           <Plus size={16} /> Generate Invoice
        </button>
      </div>

      {error && (
        <Card className="mb-10 p-8 border-[#BA0517]/10 bg-[#BA0517]/5 flex items-center gap-6 text-[#BA0517] font-bold uppercase text-[10px] rounded">
          <AlertCircle size={20} /> Connection Error: {error}
        </Card>
      )}

      <Card padding="p-0" className="overflow-hidden bg-white border-[#D8DDE6] rounded-md shadow-sm">
        <div className="px-6 py-4 border-b border-[#D8DDE6] flex items-center justify-between">
           <div className="flex items-center gap-2 text-[#080707] font-bold">
              <Receipt size={16} className="text-[#0176D3]" /> Fiscal Record Matrix
           </div>
           <div className="hidden sm:flex items-center gap-2 text-[12px] font-semibold text-[#080707]">
              <Activity size={14} className="text-[#2E844A]" /> Ledger: Up to date
           </div>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          {loading ? (
            <div className="py-40 flex flex-col items-center justify-center gap-10 opacity-30">
               <Loader size={48} className="animate-spin text-[#0176D3]" />
               <p className="text-[11px] font-bold uppercase text-[#706E6B] animate-pulse">Syncing Settlement Records…</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-60 flex flex-col items-center justify-center text-center space-y-10 opacity-40">
               <div className="w-24 h-24 rounded bg-[#ffffff] border border-[#D8DDE6] flex items-center justify-center text-[#080707] shadow-sm">
                  <Banknote size={56} strokeWidth={1} />
               </div>
               <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-[#080707] tracking-tight">No Records Located</h3>
                  <p className="text-[10px] font-bold text-[#080707] uppercase max-w-sm mx-auto leading-relaxed">System has not detected any settlement identifiers in this configuration frame.</p>
               </div>
            </div>
          ) : (
            <div className="slds2-table-wrap overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F4F6F9] text-[12px] font-semibold text-[#706E6B] border-b border-[#D8DDE6]">
                  <th className="pl-6 py-3">Invoice #</th>
                  <th className="py-3">Client Identity</th>
                  <th className="py-3">Plan</th>
                  <th className="py-3">Settlement Amount</th>
                  <th className="py-3 text-center">Status</th>
                  <th className="py-3">Preservation Date</th>
                  <th className="pr-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D8DDE6]">
                {invoices.map(inv => (
                  <tr key={inv._id} className="group hover:bg-[#F7F9FB] transition-colors border-b border-[#D8DDE6] last:border-0 cursor-default">
                    <td className="pl-6 py-4">
                       <div className="text-[14px] font-bold text-[#080707]">
                          #{inv.invoiceNumber || inv._id?.slice(-8).toUpperCase()}
                       </div>
                    </td>
                    <td className="py-4">
                       <div className="space-y-0.5">
                          <div className="text-[14px] font-bold text-[#0176D3] truncate max-w-[180px]">{inv.companyId?.companyName || inv.companyId?.name || 'Anonymous Client'}</div>
                          <div className="text-[12px] text-[#706E6B]">{inv.companyId?.email}</div>
                       </div>
                    </td>
                    <td className="py-4">
                       <Badge variant="blue" className="text-[11px] font-semibold">{inv.planId?.name || 'MANUAL CONFIG'}</Badge>
                    </td>
                    <td className="py-4">
                       <div className="text-[14px] font-bold text-[#080707] tabular-nums">
                        {formatCurrency(inv.amount?.totalAmount ?? inv.amount ?? 0, PLATFORM_CURRENCY)}
                      </div>
                    </td>
                    <td className="py-4 text-center"><InvBadge status={inv.status} /></td>
                    <td className="py-4">
                       <div className="text-[13px] text-[#706E6B]">
                          {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Data Missing'}
                       </div>
                    </td>
                    <td className="pr-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                          <button 
                            className="h-8 px-3 rounded whitespace-nowrap bg-[#E0F2FE] text-[#0176D3] text-[13px] font-semibold hover:bg-[#BAE6FD] transition-colors flex items-center gap-1"
                            onClick={() => handleMarkPaid(inv._id)}
                          >
                             <Check size={14} /> Confirm
                          </button>
                        )}
                        {inv.status === 'issued' && (
                          <button 
                            className="p-1.5 rounded-md text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE] transition-colors"
                            onClick={() => handleCancel(inv._id)}
                            title="Delete"
                          >
                             <Ban size={16} />
                          </button>
                        )}
                        <button
                          className="p-1.5 rounded-md text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] transition-colors disabled:opacity-50"
                          title="View Fiscal Record" aria-label="View Fiscal Record"
                          onClick={() => handleViewPdf(inv)}
                          disabled={downloadingId === inv._id}
                        >
                           {downloadingId === inv._id ? <Loader size={16} className="animate-spin" /> : <Eye size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Global Pagination Hub */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-[#D8DDE6] flex flex-col sm:flex-row justify-between items-center bg-[#F4F6F9] gap-4">
            <span className="text-[12px] text-[#706E6B]">
              Page <span className="font-bold text-[#080707]">{page}</span> of <span className="font-bold text-[#080707]">{totalPages}</span>
            </span>
            <div className="flex items-center gap-2">
              <button 
                disabled={page <= 1} 
                onClick={() => load(page - 1)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E5E5] bg-white text-[#706E6B] hover:bg-[#F3F2F2] disabled:opacity-50 transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
              >
                <ChevronLeft size={14} />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const n = i + Math.max(1, Math.min(page - 2, totalPages - 4));
                  if (n > totalPages || n < 1) return null;
                  return (
                    <button 
                       key={n} 
                       className={cn(
 "w-8 h-8 rounded-md text-[13px] font-semibold transition-colors flex items-center justify-center",
 page === n ? 'bg-[#0176D3] text-white border border-[#0176D3]' : 'bg-white border border-[#D8DDE6] text-[#706E6B] hover:bg-[#F3F2F2]'
 )}
                       onClick={() => load(n)}
                    >
                       {n}
                    </button>
                  );
                })}
              </div>
              <button 
                disabled={page >= totalPages} 
                onClick={() => load(page + 1)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E5E5E5] bg-white text-[#706E6B] hover:bg-[#F3F2F2] disabled:opacity-50 transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </Card>

      <CreateInvoiceModal 
        isOpen={creating} 
        onClose={() => setCreating(false)} 
        onCreated={() => { setCreating(false); load(1, statusFilter); }} 
      />
    </div>
  );
}
